import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Button, Text } from '../../../../components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import type { Influencer, InfluencerType } from '../../../../mock-data/types';
import { DEAL_TYPES } from '../../constants';
import { FieldGroup, GroupLabel, DealChips, DealChip, Helper, InfoBox, InfoTitle, InfoBody } from './InfluencerForm.styles';

export type InfluencerFormProps = {
  /** When present the form opens in edit mode (pre-filled, titled "Edit {name}"). */
  influencer?: Influencer;
  onClose: () => void;
};

type FormState = {
  partnerName: string;
  dealType: InfluencerType;
  cpaAmount: string;
  revShare: string;
  trackingLink: string;
  fixedCosts: string;
  fixedCostNote: string;
};

const initialState = (influencer?: Influencer): FormState => ({
  partnerName: influencer?.name ?? '',
  dealType: influencer?.terms?.dealType ?? 'cpa',
  cpaAmount: influencer?.terms?.cpaAmount ?? '',
  revShare: influencer?.terms?.revShare ?? '',
  trackingLink: influencer?.terms?.trackingLink ?? '',
  fixedCosts: influencer?.terms?.fixedCosts ?? '',
  fixedCostNote: influencer?.terms?.fixedCostNote ?? '',
});

/**
 * Add / Edit Influencer dialog. Reuses the shared `Overlay` for the chrome (a
 * centred modal on desktop, a bottom sheet on mobile) and the shared `Field`
 * inputs for every text field, so it reads identically to the Settings forms.
 * Pass `influencer` to open the pre-filled edit variant; omit it for the empty
 * "Add Influencer" variant.
 */
export const InfluencerForm = ({ influencer, onClose }: InfluencerFormProps): React.ReactElement => {
  const { t } = useTranslation();
  // Saving dismisses through the overlay so the form slides out; calling onClose directly
  // unmounts it on the spot and it blinks away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const isEdit = influencer !== undefined;
  const [form, setForm] = React.useState<FormState>(() => initialState(influencer));
  // Deal-type chip labels, translated reactively at the render site. The chip
  // `value` stays the stable English id used for selection/comparison.
  const dealLabels: Record<InfluencerType, string> = {
    cpa: t('CPA'),
    'rev-share': t('Rev Share'),
    hybrid: t('Hybrid'),
  };

  const setField = React.useCallback(
    (key: keyof FormState) => (value: string) => setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const onDealType = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const value = event.currentTarget.dataset.value;
    if (value === 'cpa' || value === 'rev-share' || value === 'hybrid') {
      setForm((prev) => ({ ...prev, dealType: value }));
    }
  }, []);

  const onSubmit = React.useCallback(() => {
    console.log(`oh.module.acquisition.influencers[${isEdit ? 'editPartner' : 'addPartner'}]`, form);
    dismiss();
  }, [form, isEdit, dismiss]);

  return (
    <Sheet
      title={isEdit ? t('Edit {{name}}') : t('Add Influencer')}
      titlePlaceholders={isEdit ? { name: form.partnerName } : undefined}
      ariaLabel={isEdit ? t('Edit influencer') : t('Add influencer')}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <Button
          label={isEdit ? t('Save changes') : t('Add Partner & Generate Link')}
          variant="primary"
          fullWidth
          onClick={onSubmit}
        />
      }
    >
      <Field
        label={t('Partner Name')}
        value={form.partnerName}
        placeholder="@CryptoQueen"
        onChange={setField('partnerName')}
      />

      <FieldGroup>
        <GroupLabel text="Deal Type" tag="span" />
        <DealChips>
          {DEAL_TYPES.map((option) => (
            <DealChip
              key={option.value}
              type="button"
              aria-pressed={form.dealType === option.value}
              $selected={form.dealType === option.value}
              data-value={option.value}
              onClick={onDealType}
            >
              <Text text={dealLabels[option.value]} tag="span" />
            </DealChip>
          ))}
        </DealChips>
      </FieldGroup>

      <Field
        label={t('CPA Amount')}
        value={form.cpaAmount}
        placeholder="35..."
        prefix="$"
        suffix={t('per FTD')}
        numeric
        onChange={setField('cpaAmount')}
      />

      <Field
        label={t('Revenue Share')}
        value={form.revShare}
        placeholder="30..."
        suffix="%"
        numeric
        onChange={setField('revShare')}
      />

      <Field
        label={t('Tracking Link (optional)')}
        value={form.trackingLink}
        placeholder={t('Auto-generated if empty...')}
        onChange={setField('trackingLink')}
      />

      <Field
        label={t('Fixed cost (one-time fee, optional)')}
        value={form.fixedCosts}
        placeholder="0..."
        prefix="$"
        numeric
        onChange={setField('fixedCosts')}
      />
      <Field
        label={t('Fixed cost note')}
        value={form.fixedCostNote}
        placeholder={t('e.g. Sponsorship fee...')}
        onChange={setField('fixedCostNote')}
      />
      <Helper
        text="Fixed costs will be distributed across all FTDs to calculate the true acquisition cost per player."
        tag="span"
      />

      <InfoBox>
        <InfoTitle text="Unique Tracking Link" tag="span" />
        <InfoBody
          text="A unique tracking link will be generated automatically. Share it with the influencer so all registrations are attributed correctly."
          tag="span"
        />
      </InfoBox>
    </Sheet>
  );
};

export default InfluencerForm;
